import { Edit } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useParams } from 'react-router';

import { CommentItem } from 'DesignSystem/features/history/CommentItem/CommentItem';
import {
  CommentWithReplies,
  ThreadedCommentsProvider,
  useCreateThreadedCommentHierarchy,
  useCreateThreadedCommentsConfig,
} from 'DesignSystem/features/history/CommentItem/ThreadedCommentsContext';
import CommentsAndActivities from 'DesignSystem/features/history/CommentsAndActivities';
import { SidebarHistoryItemContainer } from 'DesignSystem/features/history/Container';
import track from 'features/Analytics/track';
import useAttachments from 'features/Attachments/api';
import { postPrefCardComment, prefCardCommentsQuery, prefCardHistoryQuery } from 'pages/preference-cards/api';
import { PrefCardActivity } from 'pages/preference-cards/types';
import { queryClient } from 'utils/api';

import { createActivity } from './activity-helpers';
import AddComment from './comments/add-comment';

const activityFilter = (activity: PrefCardActivity): boolean => {
  if (activity.entity_type === 'Preference Card Item') {
    return activity.actions.some(
      (action) =>
        action.value_name === 'Snooze Until' &&
        ((action.new_value && action.old_value === null) || (action.new_value === null && action.old_value))
    );
  }

  if (activity.entity_type === 'Change Request') {
    return activity.actions.some(
      (action) =>
        action.value_name === 'Status' ||
        action.value_name === 'Reviewer' ||
        action.value_name === 'Response Type' ||
        (action.value_name === 'Response Comment' && action.new_value)
    );
  }
  return true;
};

export default function CardActivities() {
  const { cardId } = useParams<'cardId'>();
  const { data: activities } = useQuery(prefCardHistoryQuery(cardId!));
  const { data: comments } = useQuery(prefCardCommentsQuery(cardId!));
  const commentIds = React.useMemo(
    () => comments?.map(({ conversation_message_id }) => conversation_message_id).sort() || [],
    [comments]
  );
  const { data: attachmentsById } = useAttachments(commentIds);

  const threadedComments = useCreateThreadedCommentHierarchy({
    comments,
    attachmentsMap: attachmentsById,
    enabled: true,
  });

  const threadedCommentsConfig = useCreateThreadedCommentsConfig({
    moduleName: 'Physician+Preference+Cards',
    async addComment(args) {
      return await postPrefCardComment({
        cardId: cardId!,
        message: args.message,
        parentId: args.parentId,
        mentions: args.mentions,
      });
    },
    invalidateComments() {
      void queryClient.invalidateQueries(prefCardCommentsQuery(cardId!));
    },
    onComplete(args) {
      track.pcm.addComment('card', args.attachmentsCount);
    },
  });

  const sortedHistory = React.useMemo(() => {
    const commentsWithDate =
      (threadedComments &&
        threadedComments.map((comment) => {
          return {
            date: comment.created_date,
            historyType: 'Comments' as const,
            component: (
              <ThreadedCommentsProvider config={threadedCommentsConfig}>
                <CommentItem
                  comment={comment as CommentWithReplies}
                  depth={0}
                  maxDepth={1}
                  parentId={null}
                  isLastInGroup={true}
                />
              </ThreadedCommentsProvider>
            ),
          };
        })) ||
      [];

    const historyWithDate = activities?.filter(activityFilter)?.map((activity) => ({
      date: activity.state_change_time,
      historyType: 'Activities' as const,
      component: <PreferenceCardActivity key={activity.state_change_time} activity={activity} />,
    }));

    return [...(historyWithDate || []), ...(commentsWithDate || [])].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [activities, threadedComments, threadedCommentsConfig]);

  return (
    <>
      <div className="sticky top-0 z-10 mb-2 w-full bg-white p-3 shadow-[0_4px_10px_0_rgba(0,0,0,0.06)]">
        <AddComment cardId={cardId!} />
      </div>
      <CommentsAndActivities history={sortedHistory} />
    </>
  );
}

type Props = {
  activity: PrefCardActivity;
};

function PreferenceCardActivity({ activity }: Props) {
  const activityName = createActivity(activity);

  return (
    <SidebarHistoryItemContainer
      name={activity.acting_account_name}
      createdDate={activity.state_change_time}
      activity={activityName}
      icon={<Edit />}
    />
  );
}
